import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CLIENT_AUDIT_EVENTS, recordSecurityEvent } from "@/lib/securityAudit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Zap, ArrowLeft, Mail, Phone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import NoIndexSeo from "@/components/seo/NoIndexSeo";
import { safeRedirectPath } from "@/lib/safeRedirect";
import { authRedirectUrl } from "@/lib/siteConfig";
import {
  checkThrottle,
  clearThrottle,
  recordFailure,
  throttleMessage,
  formatWait,
} from "@/lib/authThrottle";
import { useBotGuard, HoneypotField } from "@/lib/botGuard";
import {
  trackPasswordResetRequested,
  trackPasswordResetEmailSent,
  trackPasswordResetRequestFailed,
} from "@/lib/appAnalytics";



const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  const [authTab, setAuthTab] = useState<string>("email");
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailGuard = useBotGuard();
  const phoneGuard = useBotGuard();

  // Same-origin relative path only; falls back to /dashboard.
  const nextPath = safeRedirectPath(searchParams.get("next"));

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Silent bot deterrent: same generic failure copy, no signal to a script.
    if (emailGuard.isLikelyBot()) {
      toast({ title: "We couldn't sign you in", description: "Check your email and password, then try again.", variant: "destructive" });
      return;
    }

    const gate = checkThrottle("sign_in", email);
    if (!gate.allowed) {
      toast({ ...throttleMessage(gate), variant: "destructive" });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      const after = recordFailure("sign_in", email);
      void recordSecurityEvent(CLIENT_AUDIT_EVENTS.signInFailed, {
        actorEmail: email,
        summary: `Password sign-in rejected: ${error.message}`,
        metadata: { status: error.status ?? null, attemptsRemaining: after.remaining, throttled: !after.allowed },
      });
      if (!after.allowed) {
        toast({ ...throttleMessage(after), variant: "destructive" });
      } else if (after.remaining <= 2) {
        toast({
          title: error.message,
          description: `${after.remaining} more ${after.remaining === 1 ? "try" : "tries"} before sign-in pauses for a while.`,
          variant: "destructive",
        });
      } else {
        toast({ title: error.message, variant: "destructive" });
      }
    } else {
      clearThrottle("sign_in", email);
      navigate(nextPath);
    }
  };

  useEffect(() => {
    if (resetCooldown <= 0) return;
    const t = window.setTimeout(() => setResetCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resetCooldown]);

  const handleForgotPassword = async () => {
    if (!email) {
      toast({ title: "Enter your email first", variant: "destructive" });
      return;
    }
    if (resetSending || resetCooldown > 0) return;

    const gate = checkThrottle("password_reset", email);
    if (!gate.allowed) {
      toast({ ...throttleMessage(gate), variant: "destructive" });
      return;
    }

    setResetSending(true);
    trackPasswordResetRequested({ has_email: true });
    void recordSecurityEvent(CLIENT_AUDIT_EVENTS.passwordResetRequested, {
      actorEmail: email,
      severity: "info",
      summary: "Password reset email requested",
    });
    const resetStartedAt = Date.now();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: authRedirectUrl("/reset-password"),
    });
    const resetDurationMs = Date.now() - resetStartedAt;
    setResetSending(false);
    if (error) {
      trackPasswordResetRequestFailed(error, resetDurationMs);
      const after = recordFailure("password_reset", email);
      void recordSecurityEvent(CLIENT_AUDIT_EVENTS.passwordResetFailed, {
        actorEmail: email,
        summary: `Password reset request rejected: ${error.message}`,
        metadata: { throttled: !after.allowed },
      });
      toast({ ...(after.allowed ? { title: error.message } : throttleMessage(after)), variant: "destructive" });
    } else {
      trackPasswordResetEmailSent(resetDurationMs);
      // Each successful send still counts, so repeated sends escalate to a block.
      const after = recordFailure("password_reset", email);
      setResetCooldown(60);
      toast({
        title: "Check your inbox",
        description: after.allowed
          ? "If an account uses that email, a reset link is on its way. It expires in one hour."
          : `Reset emails are paused for ${formatWait(after.retryAfterSeconds)}. Check your inbox and spam folder.`,
      });
    }
  };

  const handlePhoneOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) {
      toast({ title: "Enter your phone number", variant: "destructive" });
      return;
    }
    if (phoneGuard.isLikelyBot()) {
      toast({ title: "We couldn't send that code", description: "Check your number and try again.", variant: "destructive" });
      return;
    }

    const action = otpSent ? "phone_otp_verify" : "phone_otp_send";
    const gate = checkThrottle(action, phone);
    if (!gate.allowed) {
      toast({ ...throttleMessage(gate), variant: "destructive" });
      return;
    }

    setLoading(true);
    if (!otpSent) {
      const { error } = await supabase.auth.signInWithOtp({
        phone,
        options: { shouldCreateUser: false },
      });
      setLoading(false);
      // Every send counts against the window, successful or not.
      const after = recordFailure("phone_otp_send", phone);
      if (error) {
        toast({ ...(after.allowed ? { title: error.message } : throttleMessage(after)), variant: "destructive" });
      } else {
        setOtpSent(true);
        toast({ title: "Verification code sent to your phone." });
      }
    } else {
      const { error } = await supabase.auth.verifyOtp({ phone, token: otp, type: "sms" });
      setLoading(false);
      if (error) {
        const after = recordFailure("phone_otp_verify", phone);
        void recordSecurityEvent(CLIENT_AUDIT_EVENTS.signInFailed, {
          summary: `Phone OTP verification rejected: ${error.message}`,
          metadata: { method: "phone_otp", throttled: !after.allowed },
        });
        toast({ ...(after.allowed ? { title: error.message } : throttleMessage(after)), variant: "destructive" });
      } else {
        clearThrottle("phone_otp_verify", phone);
        clearThrottle("phone_otp_send", phone);
        navigate(nextPath);
      }
    }
  };


  const handleSocialSignInPaused = () => {
    toast({
      title: "Social sign-in is temporarily paused",
      description:
        "Use email and password for an existing account. Contact support if your account only uses Google or Apple.",
    });
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <NoIndexSeo
        path="/login"
        title="Log in | Myelina Health"
        description="Sign in to your Myelina Health account to review your check-ins and past energy patterns."
      />
      {/* Left. Branding panel */}
      <div className="hidden lg:flex lg:w-[45%] bg-navy text-navy-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(228,53%,18%)] via-[hsl(243,60%,30%)] to-[hsl(228,53%,12%)]" />
        <div className="absolute top-1/3 -right-20 w-[400px] h-[400px] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] rounded-full bg-emerald/5 blur-[100px]" />

        <div className="relative z-10 flex flex-col justify-between p-10 lg:p-14 w-full">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
              <Zap className="h-4.5 w-4.5 text-emerald" />
            </div>
            <span className="font-heading font-bold text-lg">BS&nbsp;of&nbsp;MS™</span>
          </Link>

          <div className="max-w-sm">
            <h2 className="font-heading text-[1.75rem] font-bold leading-tight mb-4">
              Review your energy patterns.<br />Plan with more context.
            </h2>
            <p className="text-white/50 text-[14px] leading-relaxed">
              Welcome back. Review your check-ins and the patterns you have recorded.
            </p>
          </div>

          <p className="text-white/25 text-[11px]">
            Wellness record · Not medical or emergency support
          </p>
        </div>
      </div>

      {/* Right. Form */}
      <div className="flex-1 flex items-center justify-center px-5 py-12 bg-background">
        <div className="w-full max-w-[380px]">
          {/* Mobile logo */}
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
            <Zap className="h-4 w-4 text-primary" />
            <span className="font-heading font-bold text-[15px]">BS&nbsp;of&nbsp;MS™</span>
          </Link>

          <div className="mb-8">
            <h1 className="font-heading text-2xl font-bold mb-1">Welcome back</h1>
            <p className="text-muted-foreground text-[14px]">Log in to review your check-ins</p>
          </div>

          {/* Social logins */}
          <div className="space-y-2.5 mb-5">
            <Button variant="outline" className="w-full h-11 rounded-xl text-[13px]" onClick={handleSocialSignInPaused} type="button">
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </Button>
            <Button variant="outline" className="w-full h-11 rounded-xl text-[13px]" onClick={handleSocialSignInPaused} type="button">
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C4.24 16.7 4.89 10.33 8.7 10.1c1.23.07 2.09.72 2.81.78.95-.2 1.86-.88 2.87-.8 1.22.1 2.14.58 2.73 1.48-2.5 1.5-1.9 4.82.36 5.75-.45 1.2-.99 2.37-1.93 3.46l.51-.49zM12.17 10.03c-.15-2.12 1.55-3.93 3.53-4.03.28 2.33-2.1 4.17-3.53 4.03z"/></svg>
              Continue with Apple
            </Button>
            <p id="social-login-paused-note" className="text-center text-[11px] leading-relaxed text-muted-foreground">
              Social sign-in is paused while invite-only access controls are reviewed.
            </p>
          </div>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[11px] text-muted-foreground">or continue with</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Tabs: Email / Phone */}
          <Tabs value={authTab} onValueChange={setAuthTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-10 rounded-xl mb-4">
              <TabsTrigger value="email" className="rounded-lg text-[13px] gap-1.5">
                <Mail className="h-3.5 w-3.5" /> Email
              </TabsTrigger>
              <TabsTrigger value="phone" className="rounded-lg text-[13px] gap-1.5">
                <Phone className="h-3.5 w-3.5" /> Phone
              </TabsTrigger>
            </TabsList>

            <TabsContent value="email">
              <form onSubmit={handleLogin} className="space-y-4">
                <HoneypotField guard={emailGuard} />
                <div className="space-y-2">

                  <Label className="text-[13px]">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="rounded-xl h-11" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[13px]">Password</Label>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={resetSending || resetCooldown > 0}
                      className="text-[12px] text-primary hover:underline disabled:opacity-60 disabled:no-underline"
                    >
                      {resetSending
                        ? "Sending..."
                        : resetCooldown > 0
                          ? `Resend in ${resetCooldown}s`
                          : "Forgot password?"}
                    </button>
                  </div>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-xl h-11" />
                </div>
                <Button type="submit" className="w-full h-11 rounded-xl text-[13px] font-semibold" disabled={loading}>
                  {loading ? "Logging in..." : "Log in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="phone">
              <form onSubmit={handlePhoneOtp} className="space-y-4">
                <HoneypotField guard={phoneGuard} />
                <div className="space-y-2">

                  <Label className="text-[13px]">Phone number</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    placeholder="+1 (555) 123-4567"
                    className="rounded-xl h-11"
                    disabled={otpSent}
                  />
                </div>
                {otpSent && (
                  <div className="space-y-2">
                    <Label className="text-[13px]">Verification code</Label>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                      placeholder="123456"
                      maxLength={6}
                      className="rounded-xl h-11 tracking-[0.3em] text-center font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => { setOtpSent(false); setOtp(""); }}
                      className="text-[12px] text-primary hover:underline"
                    >
                      Change number
                    </button>
                  </div>
                )}
                <Button type="submit" className="w-full h-11 rounded-xl text-[13px] font-semibold" disabled={loading}>
                  {loading ? "Verifying..." : otpSent ? "Verify & log in" : "Send code"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-center text-[13px] text-muted-foreground mt-8">
            Don't have an account?{" "}
            <Link to="/signup" className="text-primary font-medium hover:underline">Sign up with invite code</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
